import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Button, 
  Tabs, 
  Tab, 
  AppBar, 
  Toolbar,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import VenuesList from '../components/admin/VenuesList';
import CsvImporter from '../components/admin/CsvImporter';
import SuggestionsTable from '../components/admin/SuggestionsTable';
import DealSuggestionsTable from '../components/admin/DealSuggestionsTable';

const AdminDashboardPage = () => {
  const { currentAdmin, logOut } = useAdminAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // You could fetch initial data here
    document.title = 'Admin Dashboard - Happy Hours TLV';
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('Failed to log out:', error);
      setAlert({
        open: true,
        message: 'Failed to log out. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleAddVenue = () => {
    navigate('/admin/venues/new');
  };

  const handleAlertClose = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Admin AppBar */}
      <AppBar position="static" color="primary">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Happy Hours TLV Admin
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {currentAdmin?.email}
            </Typography>
            
            <Button 
              color="inherit" 
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
        
        <Box sx={{ bgcolor: 'background.paper' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Venues" />
            <Tab label="Suggestions" />
            <Tab label="Settings" />
          </Tabs>
        </Box>
      </AppBar>
      
      {/* Main content area */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {/* Venues Tab Panel */}
            {activeTab === 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h4" component="h1">
                    Manage Happy Hour Venues
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleAddVenue}
                    >
                      Add New Venue
                    </Button>
                    <CsvImporter 
                      onComplete={(message) => {
                        setAlert({
                          open: true,
                          message,
                          severity: 'success'
                        });
                      }}
                      onImportSuccess={() => setRefreshTrigger(prev => prev + 1)}
                    />
                  </Box>
                </Box>
                
                <Paper elevation={2} sx={{ p: 2 }}>
                  <VenuesList key={refreshTrigger} />
                </Paper>
              </Box>
            )}
            
            {/* Suggestions Tab Panel */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                  Suggestions & New Deals
                </Typography>
                
                <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Venue Correction Suggestions
                  </Typography>
                  <SuggestionsTable />
                </Paper>

                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    New Deal Suggestions
                  </Typography>
                  <DealSuggestionsTable />
                </Paper>
              </Box>
            )}
                     
            {/* Settings Tab Panel */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
                  System Settings
                </Typography>
                
                <Paper elevation={2} sx={{ p: 2, minHeight: '300px' }}>
                  <Typography>Settings and configuration options will appear here</Typography>
                </Paper>
              </Box>
            )}
          </>
        )}
      </Container>
      
      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} Happy Hours TLV Admin Portal
          </Typography>
        </Container>
      </Box>
      
      {/* Alert/Snackbar for notifications */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleAlertClose} 
          severity={alert.severity} 
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboardPage;